<template>
  <div class="report-page">
    <div class="filter-card">
      <div class="fc-row fc-row-primary fc-row-with-search">
        <div class="field">
          <label class="field-lbl">ช่วงวันที่</label>
          <date-picker
            v-model="dateRange"
            type="date"
            range
            format="DD MMM YY"
            :shortcuts="dateRangeShortcuts"
            placeholder="ทุกวันที่"
            input-class="form-control form-control-sm field-input-native"
          ></date-picker>
        </div>
        <div class="field">
          <label class="field-lbl" :for="`basis-${payer}`">อิงตาม</label>
          <b-form-select :id="`basis-${payer}`" v-model="dateBasis" :options="basisOptions" size="sm" class="field-input-native" />
        </div>
        <div class="field">
          <label class="field-lbl" :for="`q-${payer}`"><i class="fas fa-search"></i> ค้นหา</label>
          <b-form-input
            :id="`q-${payer}`"
            v-model.trim="keyword"
            type="search"
            size="sm"
            class="field-input-native"
            :placeholder="isNhso ? 'เลขบัตร ปชช., ชื่อ, เลขที่ใบเสร็จ หรือเลขที่ REP' : 'เลขบัตร ปชช., ชื่อ, เลขที่ใบเสร็จ หรือเลขที่เอกสาร'"
            @keyup.enter="apply"
          />
        </div>
        <b-button variant="primary" class="btn-primary-lg" @click="apply">แสดงข้อมูล</b-button>
      </div>
      <div class="chip-group">
        <small class="text-muted mr-1">ช่วงด่วน</small>
        <button
          v-for="c in chips"
          :key="c.key"
          type="button"
          class="chip"
          :class="{ 'chip-active': activeChip === c.key }"
          :aria-pressed="activeChip === c.key"
          @click="pickChip(c.key)"
        >
          {{ c.key === "all" ? allLabel : c.label }}
        </button>
      </div>
    </div>

    <div class="recon-tiles" role="group" aria-label="สถานะ">
      <button
        v-for="t in tiles"
        :key="t.key"
        type="button"
        class="recon-tile"
        :class="[`recon-tile-${t.key}`, { active: status === t.key }]"
        :aria-pressed="status === t.key"
        @click="status = t.key"
      >
        <span class="recon-tile-name"><span class="recon-dot"></span>{{ t.label }}</span>
        <span class="recon-tile-num">
          ฿{{ formatAmount(t.amount) }}<small>{{ t.count.toLocaleString("th-TH") }} {{ t.unit }}</small>
        </span>
        <span class="recon-tile-sub">{{ t.sub }}</span>
      </button>
    </div>

    <OutstandingTab v-if="status === 'outstanding'" :payer="payer" :filter="applied" @paid="refresh" />
    <PaidTab v-else-if="status === 'paid'" :payer="payer" :filter="applied" @undone="refresh" />
    <NeedsReviewTab v-else :payer="payer" :filter="applied" @matched="refresh" />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import moment from "moment";
import { allRangeLabel, quickRange, widenRangeToInclude } from "@/utils/reconciliation";
import OutstandingTab from "@/components/reconciliation/OutstandingTab.vue";
import PaidTab from "@/components/reconciliation/PaidTab.vue";
import NeedsReviewTab from "@/components/reconciliation/NeedsReviewTab.vue";

const PAYER_LABEL = { SSO: "ประกันสังคม", NHSO: "สปสช." };
const toDay = (d) => (d ? moment(d).format("YYYY-MM-DD") : "");

/**
 * One payer's reconciliation: a single filter card drives the three status tiles and the table
 * under them (ค้างรับ always filters on the service date; รับแล้ว follows อิงตาม; รอตรวจสอบ on the
 * statement line's date). The filter applies on แสดงข้อมูล / Enter / a ช่วงด่วน chip.
 * It opens on ทั้งหมด: payers pay weekly, so the working list is everything still owed since go-live.
 */
export default {
  name: "PayerPanel",
  components: { OutstandingTab, PaidTab, NeedsReviewTab },
  props: {
    payer: { type: String, required: true },
    // { dateFrom, dateTo } to open with instead of ทั้งหมด (link from the ลูกหนี้ report)
    initialRange: { type: Object, default: null },
  },
  data() {
    const start = this.initialRange || quickRange("all");
    const toDate = (d) => (d ? moment(d).toDate() : null);
    return {
      dateRange: start.dateFrom || start.dateTo ? [toDate(start.dateFrom), toDate(start.dateTo)] : [],
      dateBasis: "service",
      keyword: "",
      activeChip: this.initialRange ? null : "all",
      applied: { dateFrom: start.dateFrom, dateTo: start.dateTo, dateBasis: "service", keyword: "", nonce: 0 },
      status: "outstanding",
      totals: null,
      basisOptions: [
        { value: "service", text: "วันที่ใช้สิทธิ" },
        { value: "received", text: "วันที่รับเงิน" },
      ],
      chips: [
        { key: "thisMonth", label: "เดือนนี้" },
        { key: "lastMonth", label: "เดือนที่แล้ว" },
        { key: "last60", label: "ย้อนหลัง 60 วัน" },
        { key: "all", label: "ทั้งหมด" },
      ],
    };
  },
  computed: {
    ...mapGetters({ dateRangeShortcuts: "moduleMaster/getDateRangeShortcuts" }),
    isNhso() {
      return this.payer === "NHSO";
    },
    allLabel() {
      return allRangeLabel(this.totals?.startDate);
    },
    unit() {
      return this.isNhso ? "visit" : "รายการ";
    },
    tiles() {
      const t = this.totals || { outstanding: { count: 0, amount: 0 }, paid: { count: 0, amount: 0 }, review: { count: 0, amount: 0 } };
      return [
        { key: "outstanding", label: "ค้างรับ", ...t.outstanding, unit: this.unit,
          sub: this.isNhso ? "700 บาทต่อ visit · รอเงินจาก สปสช." : `รอเงินจาก${PAYER_LABEL[this.payer]}` },
        { key: "paid", label: "รับแล้ว", ...t.paid, unit: this.unit,
          sub: this.applied.dateBasis === "received" ? "นับตามวันที่รับเงิน" : "นับตามวันที่ใช้สิทธิ" },
        { key: "review", label: "รอตรวจสอบ", ...t.review, unit: "รายการ", sub: "บรรทัดในเอกสารที่ยังจับคู่ไม่ได้" },
      ];
    },
    clinicUrl() {
      return this.$route.params.clinicUrl;
    },
    branchUrl() {
      return this.$route.params.branchUrl;
    },
  },
  mounted() {
    this.loadTotals();
  },
  methods: {
    ...mapActions("moduleReconciliation", ["fetchTotals"]),
    apply() {
      const [from, to] = this.dateRange || [];
      this.applied = {
        dateFrom: toDay(from),
        dateTo: toDay(to),
        dateBasis: this.dateBasis,
        keyword: this.keyword,
        nonce: this.applied.nonce + 1,
      };
      this.loadTotals();
    },
    pickChip(key) {
      this.activeChip = key;
      const r = quickRange(key);
      this.dateRange = r.dateFrom ? [moment(r.dateFrom).toDate(), moment(r.dateTo).toDate()] : [];
      this.apply();
    },
    async loadTotals() {
      try {
        this.totals = await this.fetchTotals({
          clinicUrl: this.clinicUrl,
          branchUrl: this.branchUrl,
          payer: this.payer,
          dateFrom: this.applied.dateFrom,
          dateTo: this.applied.dateTo,
          dateBasis: this.applied.dateBasis,
        });
      } catch {
        // the tables show their own errors; keep the previous totals
      }
      this.$emit("totals", this.totals);
    },
    // a mark-paid / undo / match changed the numbers
    refresh() {
      this.loadTotals();
    },
    // Called by the page after an import into this payer. When the imported lines fall outside the
    // filter, the range is stretched to cover them (and today, the received date, under วันที่รับเงิน)
    // so the import never looks like it did nothing; returns the new range, or null if unchanged.
    onImported({ unmatched, dateFrom, dateTo } = {}) {
      this.status = unmatched > 0 ? "review" : "paid";
      const dates = [dateFrom, dateTo];
      if (this.applied.dateBasis === "received") dates.push(moment().format("YYYY-MM-DD"));
      const widened = widenRangeToInclude(this.applied, dates);
      if (widened) {
        this.dateRange = [moment(widened.dateFrom).toDate(), moment(widened.dateTo).toDate()];
        this.activeChip = null;
      }
      this.applied = { ...this.applied, ...widened, nonce: this.applied.nonce + 1 };
      this.loadTotals();
      return widened;
    },
    formatAmount(n) {
      return Number(n || 0).toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
  },
};
</script>

<style scoped>
.recon-tiles {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 14px;
}
.recon-tile {
  text-align: left;
  background: #fff;
  border: 1px solid #e5e9ef;
  border-radius: 10px;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-height: 44px;
}
.recon-tile.active {
  border-color: #2a6df4;
  box-shadow: 0 0 0 1px #2a6df4;
  background: #f7faff;
}
.recon-tile-review.active {
  border-color: #c98a0a;
  box-shadow: 0 0 0 1px #c98a0a;
  background: #fffbf2;
}
.recon-tile-name {
  font-size: 12.5px;
  font-weight: 600;
  color: #4a5568;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.recon-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #2a6df4;
}
.recon-tile-paid .recon-dot {
  background: #28a745;
}
.recon-tile-review .recon-dot {
  background: #d48806;
}
.recon-tile-num {
  font-size: 22px;
  font-weight: 700;
  color: #1f2933;
  font-variant-numeric: tabular-nums;
  line-height: 1.3;
}
.recon-tile-num small {
  font-size: 12px;
  font-weight: 500;
  color: #5f6b7a;
  margin-left: 6px;
}
.recon-tile-sub {
  font-size: 11.5px;
  color: #5f6b7a;
}
@media (max-width: 991px) {
  .recon-tiles {
    grid-template-columns: 1fr;
  }
}
</style>
